import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, Download, CheckCircle, AlertCircle, FileText, Info, Loader2 } from 'lucide-react';
import { PersonFinal } from '@/api/entities';
import { Progress } from '@/components/ui/progress';
import { tenantIdForStoredKey, tenantIdsEqual } from '@/lib/tenantUtils';

// Helper function to delay execution
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

async function writePersonRoleTitle(person, targetTenantId, row) {
  const isOwnTenant = tenantIdsEqual(person.tenant_id, targetTenantId);
  const updateData = {
    role_title: row.role_title || null
  };
  if (row.person_validation_url) {
    updateData.person_validation_url = row.person_validation_url;
  }
  if (isOwnTenant) {
    await PersonFinal.update(person.id, updateData);
    return;
  }
  const currentOverrides = person.tenant_overrides || {};
  const tenantOverride = {
    ...(currentOverrides[targetTenantId] || {}),
    ...updateData
  };
  await PersonFinal.update(person.id, {
    tenant_overrides: {
      ...currentOverrides,
      [targetTenantId]: tenantOverride
    }
  });
}

// Proper CSV parser that handles quoted fields with commas
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  // Add the last field
  result.push(current.trim());
  
  return result;
};

export default function PersonRoleTitleUploader() {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const fileName = selectedFile.name.toLowerCase();
    const isCSV = fileName.endsWith('.csv') || selectedFile.type === 'text/csv' || selectedFile.type === 'application/vnd.ms-excel';
    
    if (isCSV) {
      setFile(selectedFile);
      setError(null);
      setResults(null);
    } else {
      setError('Please select a valid CSV file (must end with .csv)');
      setFile(null);
    }
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must contain a header row and at least one data row');
    }

    const headerValues = parseCSVLine(lines[0]);
    const headers = headerValues.map(h => h.toLowerCase());
    
    console.log('📋 CSV Headers:', headers);
    
    const requiredColumns = ['tenant_id', 'person_id', 'role_title'];
    const missingRequired = requiredColumns.filter(col => !headers.includes(col));
    
    if (missingRequired.length > 0) {
      throw new Error(`Missing required columns: ${missingRequired.join(', ')}`);
    }

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      
      if (values.length !== headers.length) {
        console.warn(`Row ${i + 1} has ${values.length} columns, expected ${headers.length}. Row: ${lines[i]}`);
        // Still try to process if we have the minimum required fields
      }

      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });

      if (row.person_id && row.role_title) {
        console.log(`✅ Row ${i}: ${row.person_id} -> "${row.role_title}" (${row.role_title.length} chars)`);
        data.push(row);
      } else {
        console.warn(`⚠️ Row ${i + 1} missing required data, skipping`);
      }
    }

    return data;
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResults(null);
    setProgress({ current: 0, total: 0 });

    try {
      const text = await file.text();
      const data = parseCSV(text);

      console.log('📊 Parsed CSV data:', data);
      setProgress({ current: 0, total: data.length });

      const allPeople = await PersonFinal.list();
      
      let updated = 0;
      let notFound = 0;
      const errors = [];

      // Process records with delay to avoid rate limiting
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        
        try {
          const targetTenantId = tenantIdForStoredKey(row.tenant_id);
          if (!targetTenantId) {
            errors.push(`Row ${i + 1}: missing tenant_id`);
            setProgress({ current: i + 1, total: data.length });
            continue;
          }
          const personId = String(row.person_id || '').trim();
          if (!personId) {
            errors.push(`Row ${i + 1}: missing person_id`);
            setProgress({ current: i + 1, total: data.length });
            continue;
          }
          
          // Find person by person_id
          const person = allPeople.find(p => 
            p.person_id === personId || p.id === personId
          );

          if (!person) {
            notFound++;
            errors.push(`Person with ID "${personId}" not found`);
            console.warn(`❌ Person not found: ${personId}`);
            setProgress({ current: i + 1, total: data.length });
            continue;
          }

          console.log(`🔍 Found person "${person.first_name} ${person.last_name}" (${person.person_id}), updating role_title (${row.role_title.length} chars) for tenant: ${targetTenantId}`);

          await writePersonRoleTitle(person, targetTenantId, row);

          updated++;
          
          // Add delay to prevent rate limiting (500ms between requests)
          if (i < data.length - 1) {
            await delay(500);
          }
          
        } catch (err) {
          console.error(`Error updating person ${row.person_id}:`, err);
          
          // If it's a rate limit error, add a longer delay and retry once
          if (err.response?.status === 429 || err.message?.includes('Rate limit')) {
            console.warn(`⏸️ Rate limit hit on ${row.person_id}, waiting 2 seconds and retrying...`);
            await delay(2000);
            
            try {
              // Retry the update
              const person = allPeople.find(p => 
                p.person_id === row.person_id.trim() || p.id === row.person_id.trim()
              );
              
              if (person) {
                const retryTenantId = tenantIdForStoredKey(row.tenant_id);
                if (!retryTenantId) {
                  errors.push(`Failed to update ${row.person_id} after retry: missing tenant_id`);
                } else {
                  await writePersonRoleTitle(person, retryTenantId, row);
                  updated++;
                  console.log(`✅ Retry successful for ${row.person_id}`);
                }
              }
            } catch (retryErr) {
              errors.push(`Failed to update ${row.person_id} after retry: ${retryErr.message}`);
            }
          } else {
            errors.push(`Failed to update ${row.person_id}: ${err.message}`);
          }
        }
        
        setProgress({ current: i + 1, total: data.length });
      }

      setResults({
        total: data.length,
        updated,
        notFound,
        errors
      });

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to process CSV file');
    } finally {
      setIsProcessing(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const downloadTemplate = () => {
    const template = 'tenant_id,person_id,role_title,person_validation_url\ncalmedica,per_001,"Chief Medical Officer, Director of Innovation",https://example.com/validation\ncalmedica,per_002,"Directrice de l\'offre, de la performance et de la télémédecine (DOP)",https://example.com/validation2\ncalmedica,per_003,Director of Patient Services,https://example.com/validation3';
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'person_role_title_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const progressPercentage = progress.total > 0 ? (progress.current / progress.total) * 100 : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#00C2C2]" />
          Person Role Title Bulk Uploader
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-blue-800">
            <strong>Multi-tenant support:</strong> When uploading data for a tenant that doesn&apos;t own the person record, 
            the data is stored as a tenant override. This allows each tenant to have their own view of role titles without affecting other tenants.
            <br /><br />
            <strong>Rate limiting:</strong> Updates are processed with a 500ms delay between each record to prevent rate limit errors.
            <br /><br />
            <strong>CSV Format:</strong> Use quotes around fields that contain commas. Example: <code className="bg-white px-1 py-0.5 rounded text-xs">&quot;Director, Innovation and Strategy&quot;</code>
          </AlertDescription>
        </Alert>

        <div className="text-sm text-gray-600">
          <p className="mb-2">Upload a CSV file to bulk update person role titles and validation URLs.</p>
          <p className="mb-2 font-semibold">Required columns:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><code className="bg-gray-100 px-1 py-0.5 rounded text-xs">tenant_id</code> - The tenant identifier (e.g., instructure, calmedica)</li>
            <li><code className="bg-gray-100 px-1 py-0.5 rounded text-xs">person_id</code> - The person&apos;s unique identifier</li>
            <li><code className="bg-gray-100 px-1 py-0.5 rounded text-xs">role_title</code> - The role title (use quotes if it contains commas, up to 500 characters)</li>
          </ul>
          <p className="mt-2 mb-1 font-semibold">Optional columns:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><code className="bg-gray-100 px-1 py-0.5 rounded text-xs">person_validation_url</code> - URL to validate the person&apos;s information</li>
          </ul>
        </div>

        <Button
          variant="outline"
          onClick={downloadTemplate}
          className="w-full"
        >
          <Download className="w-4 h-4 mr-2" />
          Download CSV Template
        </Button>

        <div className="border-2 border-dashed border-gray-300 hover:border-gray-400 rounded-lg p-6 text-center transition-colors">
          <input
            type="file"
            accept=".csv,text/csv,application/vnd.ms-excel"
            onChange={handleFileChange}
            className="hidden"
            id="csv-upload-person-role"
            disabled={isProcessing}
          />
          <label htmlFor="csv-upload-person-role" className={`${isProcessing ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} block`}>
            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm font-medium text-gray-700 mb-1">
              {file ? '✓ File selected' : 'Click to select CSV file'}
            </p>
            {file && (
              <p className="text-xs text-gray-500">{file.name}</p>
            )}
            {!file && (
              <p className="text-xs text-gray-500">or drag and drop</p>
            )}
          </label>
        </div>

        {file && (
          <Button
            onClick={handleUpload}
            disabled={isProcessing}
            className="w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing... {progress.current}/{progress.total}
              </>
            ) : (
              'Upload and Process'
            )}
          </Button>
        )}

        {isProcessing && progress.total > 0 && (
          <div className="space-y-2">
            <Progress value={progressPercentage} className="h-2" />
            <p className="text-xs text-center text-gray-600">
              Processing {progress.current} of {progress.total} records ({Math.round(progressPercentage)}%)
            </p>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {results && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold text-green-800">Upload Complete</p>
                <div className="text-sm text-green-700">
                  <p>Total records: {results.total}</p>
                  <p>Successfully updated: {results.updated}</p>
                  <p>Not found: {results.notFound}</p>
                </div>
                {results.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-semibold text-orange-700">Errors:</p>
                    <ul className="text-xs text-orange-600 list-disc list-inside max-h-32 overflow-y-auto">
                      {results.errors.slice(0, 10).map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                      {results.errors.length > 10 && (
                        <li>... and {results.errors.length - 10} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}